// Scene 1 - Landschaften + Werke.
// Portiert aus Mock: renderLandscape, renderWerkList, drawFan, updateFanState.
// Die Bezier-Fanlinien werden nach dem Rendering von QAS-Anker zu Werk-Karten
// gezeichnet und laufen selbstzeichnend ein.

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useLang } from "../i18n";
import { useTransportV2, hasWerke, allWerke, activeWerke } from "../context/TransportV2Context";
import { Cx } from "../components/IconSprite";
import { _prepLine } from "../components/PipelineSvg";
import { usePipelineReady } from "../hooks/usePipelineReady";

function isReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
}

export default function Scene1Landscape() {
  const { t } = useLang();
  const { state, dispatch } = useTransportV2();
  const pipe = usePipelineReady(state.spaceId);
  const sapModus = pipe.modus === "sap";
  const space = state.space;
  const werkeList = useMemo(() => allWerke(state), [state]);
  const activeCount = useMemo(() => activeWerke(state).length, [state]);
  const fanBoxRef = useRef(null);
  const fanSvgRef = useRef(null);
  const fanAnchorRef = useRef(null);
  const werkListRef = useRef(null);

  const drawFan = useCallback(() => {
    const box = fanBoxRef.current;
    const svg = fanSvgRef.current;
    const anchor = fanAnchorRef.current;
    const werkListEl = werkListRef.current;
    if (!box || !svg || !anchor || !werkListEl) return;
    if (!box.offsetParent) return;
    const W = box.clientWidth, H = box.clientHeight;
    svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
    const rel = (el) => {
      const r = el.getBoundingClientRect();
      const b = box.getBoundingClientRect();
      return { l: r.left - b.left, r: r.right - b.left, cy: r.top - b.top + r.height / 2 };
    };
    const q = rel(anchor);
    // ⛔ Kein innerHTML hier. Die Werk-Kennung ist Freitext aus den Instanz-Einstellungen.
    // React maskiert sie beim Schreiben von data-werk, ueber card.dataset.werk kaeme sie
    // aber roh zurueck und wuerde als Markup geparst - im SVG-Namensraum fuehrt ein
    // Ereignisattribut wie onbegin dann fremden Code aus, und zwar im Browser jedes
    // Betrachters. setAttribute parst nichts, damit ist der Weg zu.
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    for (const card of werkListEl.querySelectorAll(".werk-card")) {
      const r = rel(card);
      const x1 = q.r + 4, x2 = r.l - 4;
      const d = `M ${x1} ${q.cy} C ${x1 + (x2 - x1) * 0.45} ${q.cy}, ${x2 - (x2 - x1) * 0.45} ${r.cy}, ${x2} ${r.cy}`;
      const werk = card.dataset.werk;
      const linie = document.createElementNS("http://www.w3.org/2000/svg", "path");
      linie.setAttribute("class", `pl-line fan-line ${state.werkOn[werk] ? "" : "off"}`);
      linie.setAttribute("data-fan", werk);
      linie.setAttribute("d", d);
      linie.style.stroke = "var(--signal)";
      svg.appendChild(linie);
    }
    const lines = [...svg.querySelectorAll(".fan-line")];
    lines.forEach(_prepLine);
    lines.forEach((l, i) => {
      setTimeout(() => l.classList.add("draw"), isReducedMotion() ? 0 : 150 + i * 140);
    });
  }, [state.werkOn]);

  useEffect(() => {
    if (!hasWerke(state)) return;
    const t = setTimeout(drawFan, 80);
    return () => clearTimeout(t);
  }, [state, drawFan]);

  useEffect(() => {
    const onResize = () => hasWerke(state) && drawFan();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [state, drawFan]);

  // Fanlinien-Opacity live bei Werk-Toggle updaten
  useEffect(() => {
    const svg = fanSvgRef.current;
    if (!svg) return;
    for (const l of svg.querySelectorAll(".fan-line")) {
      const key = l.dataset.fan;
      const on = state.werkOn[key];
      l.classList.toggle("off", !on);
    }
  }, [state.werkOn]);

  if (!space) return null;

  const stages = space.stages || [];
  const tgtIdx = Math.max(0, stages.length - 2);
  const src = stages[0];
  const tgt = stages[tgtIdx];
  const rel = stages[stages.length - 1];
  const meta = t("s1.meta", { name: space.name || "", n: stages.length }) + (hasWerke(state) ? "" : " · " + t("s3.single"));

  return (
    <section className="scene active scene-enter" id="s1">
      <div className="sh">
        <h2>{t("s1.h2")}</h2>
        <p>{t("s1.p")}</p>
      </div>

      <div className="panel">
        <div className="panel-h">
          <h3>{t("s1.landTitle")}</h3>
          <span className="meta">{meta}</span>
        </div>
        <div className="land-row">
          {stages.map((st, i) => {
            const mod = i === 0 ? "stagecard--src" : i === tgtIdx ? "stagecard--tgt" : i === stages.length - 1 ? "stagecard--dim" : "";
            const role = i === 0 ? t("role.src") : i === tgtIdx ? t("role.tgt") : i === stages.length - 1 ? t("role.rel") : t("role.pipe");
            return (
              <React.Fragment key={"stg" + i}>
                {i > 0 && <div className="land-sep"><Cx /></div>}
                <div className={"stagecard " + mod + " stagger-in"} style={{ "--i": i }}>
                  <div className="row1">
                    <span className="gl"><svg className="ic"><use href={"#" + (st.glyph || "ic-stage-dev")} /></svg></span>
                    <span className="code">{st.code}</span>
                    <span className="role">{role}</span>
                  </div>
                  <div className="inst">{st.inst}</div>
                  <div className="note">{st.name}</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div className="land-info">
          <svg className="ic ic-16"><use href="#ic-info" /></svg>
          <span>{t(sapModus ? "s1.infoSap" : "s1.info", { src: src?.code || "DEV", tgt: tgt?.code || "QAS", rel: rel?.code || "PROD", cx: <Cx /> })}</span>
        </div>
      </div>

      {hasWerke(state) && (
        <div className="sect">
          <div className="panel">
            <div className="panel-h">
              <h3>{t("s1.werkTitle")}</h3>
              <span className="meta">
                {activeCount
                  ? t("s1.werkMeta", { x: activeCount, n: werkeList.length })
                  : t("s1.werkNone")}
              </span>
            </div>
            <div className="fanbox" ref={fanBoxRef}>
              <svg className="fansvg" ref={fanSvgRef} aria-hidden="true" />
              <div className="fan-anchor" ref={fanAnchorRef}>
                <div className="stagecard stagecard--tgt" style={{ minWidth: 0 }}>
                  <div className="row1">
                    <span className="gl"><svg className="ic"><use href="#ic-stage-qas" /></svg></span>
                    <span className="code">{tgt?.code || "QAS"}</span>
                    <span className="role">{t("role.tgt")}</span>
                  </div>
                  <div className="inst">{tgt?.inst || ""}</div>
                  <div className="note">{t("s1.qasNote")}</div>
                </div>
              </div>
              <div />
              <div className="werk-list" ref={werkListRef}>
                {werkeList.map((w, i) => {
                  const on = !!state.werkOn[w.key];
                  return (
                    <button
                      key={w.key || w.kennung}
                      type="button"
                      className={"werk-card" + (on ? " on" : "") + " stagger-in"}
                      style={{ "--i": i }}
                      data-werk={w.key}
                      aria-pressed={on}
                      onClick={() => dispatch({ type: "TOGGLE_WERK", payload: w.key })}
                    >
                      <span className="wc-check">
                        <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12.5 L10 17.5 L19 6.5" />
                        </svg>
                      </span>
                      <span>
                        <span className="wc-name">{w.name}</span>
                        <span className="wc-sub" style={{ display: "block" }}>{t("s1.werkSub")}</span>
                      </span>
                      <span className="wc-ken">{w.kennung}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="footbar">
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 0 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <button className="btn btn-cta" onClick={() => dispatch({ type: "SET_STEP", payload: 2 })}>
          {t("s1.next")}
          <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
        </button>
      </div>
    </section>
  );
}
